import React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

export const _frontmatter = {
  "title": "The `sx` Prop"
};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Note = makeShortcode("Note");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <h1 {...{
      "id": "the-sx-prop"
    }}>{`The `}<inlineCode parentName="h1">{`sx`}</inlineCode>{` Prop`}</h1>
    <p>{`The `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop lets you style elements inline, using values from your theme.
To use the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop, add the custom `}<inlineCode parentName="p">{`/** @jsx jsx */`}</inlineCode>{` pragma comment to the top of your module and import the `}<inlineCode parentName="p">{`jsx`}</inlineCode>{` function.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`/** @jsx jsx */
import { jsx } from 'theme-ui'

export default props => (
  <div
    {...props}
    sx={{
      // values referencing scales defined in a theme
      color: 'primary',
      bg: 'lightgray',
      fontFamily: 'body',
      // raw CSS value
      boxShadow: '0 0 1px 3px rgba(0, 0, 0, .125)',
    }}
  />
)
`}</code></pre>
    <Note mdxType="Note">
      <p>{`Under the hood, Theme UI uses a `}<a parentName="p" {...{
          "href": "/jsx-pragma"
        }}>{`custom pragma comment`}</a>{` that converts a theme-aware `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop into a style object and passes it to the Emotion's `}<inlineCode parentName="p">{`jsx`}</inlineCode>{` function.
The `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop only works in modules that have defined a custom pragma at the top of the file, which replaces the default `}<inlineCode parentName="p">{`React.createElement`}</inlineCode>{` function.
This means you can control which modules in your application opt into this feature without the need for a Babel plugin or additional configuration.
This is intended as a complete replacement for the Emotion custom JSX pragma.`}</p>
    </Note>
    <h2 {...{
      "id": "theme-aware-properties"
    }}>{`Theme-Aware Properties`}</h2>
    <p>{`The following CSS properties will use values defined in the theme, when available.`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Property`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Theme Key`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`fontFamily`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`fonts`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`fontSize`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`fontSizes`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`fontWeight`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`fontWeights`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`lineHeight`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`lineHeights`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`letterSpacing`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`letterSpacings`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`color`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`colors`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`backgroundColor`}</inlineCode>{`, `}<inlineCode parentName="td">{`bg`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`colors`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`margin`}</inlineCode>{`, `}<inlineCode parentName="td">{`m`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`marginTop`}</inlineCode>{`, `}<inlineCode parentName="td">{`mt`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`marginRight`}</inlineCode>{`, `}<inlineCode parentName="td">{`mr`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`marginBottom`}</inlineCode>{`, `}<inlineCode parentName="td">{`mb`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`marginLeft`}</inlineCode>{`, `}<inlineCode parentName="td">{`ml`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`marginX`}</inlineCode>{`, `}<inlineCode parentName="td">{`mx`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`marginY`}</inlineCode>{`, `}<inlineCode parentName="td">{`my`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`padding`}</inlineCode>{`, `}<inlineCode parentName="td">{`p`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`paddingTop`}</inlineCode>{`, `}<inlineCode parentName="td">{`pt`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`paddingRight`}</inlineCode>{`, `}<inlineCode parentName="td">{`pr`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`paddingBottom`}</inlineCode>{`, `}<inlineCode parentName="td">{`pb`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`paddingLeft`}</inlineCode>{`, `}<inlineCode parentName="td">{`pl`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`paddingX`}</inlineCode>{`, `}<inlineCode parentName="td">{`px`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`paddingY`}</inlineCode>{`, `}<inlineCode parentName="td">{`py`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`top`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`bottom`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`left`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`right`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`space`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`border`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`borders`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`borderTop`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`borders`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`borderRight`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`borders`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`borderBottom`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`borders`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`borderLeft`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`borders`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`borderColor`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`colors`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`borderWidth`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`borderWidths`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`borderStyle`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`borderStyles`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`borderRadius`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`radii`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`boxShadow`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`shadows`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`textShadow`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`shadows`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`zIndex`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`zIndices`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`width`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sizes`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`minWidth`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sizes`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`maxWidth`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sizes`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`height`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sizes`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`minHeight`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sizes`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`maxHeight`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sizes`}</inlineCode></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`size`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`sizes`}</inlineCode></td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "responsive-values"
    }}>{`Responsive Values`}</h2>
    <p>{`Theme UI, like Styled System, includes a shorthand syntax for writing mobile-first responsive styles using arrays as values.
This is useful when you want to change a single property across multiple breakpoints without needing to write verbose media query syntax.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`/** @jsx jsx */
import { jsx } from 'theme-ui'

export default props => (
  <div
    {...props}
    sx={{
      width: ['100%', '50%', '25%'],
    }}
  />
)
`}</code></pre>
    <h3 {...{
      "id": "skipping-breakpoints"
    }}>{`Skipping Breakpoints`}</h3>
    <p>{`If you want to skip a breakpoint, you can use the value `}<inlineCode parentName="p">{`null`}</inlineCode>{`. This is useful if you want to set a value for only the largest breakpoint, for example.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`/** @jsx jsx */
import { jsx } from 'theme-ui'

export default props => (
  <div
    {...props}
    sx={{
      width: [null, null, '25%'],
    }}
  />
)
`}</code></pre>
    <h2 {...{
      "id": "margin-and-padding"
    }}>{`Margin and Padding`}</h2>
    <p>{`Margin and padding are treated as first-class citizens in Theme UI,
using values defined in the `}<inlineCode parentName="p">{`theme.space`}</inlineCode>{` scale, and include an optional shorthand syntax for using negative space in your application.`}</p>
    <p>{`In addition to shorthands for applying margin and padding on the x- and y-axes, a terse naming convention can be used to quickly edit styles.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`// example of using margin and padding shorthand syntax
<div
  sx={{
    px: 3, // padding-left & padding-right
    // paddingX: 3 will also work
    py: 4, // padding-top & padding-bottom
    mb: 3, // margin-bottom
  }}
/>
`}</code></pre>
    <h2 {...{
      "id": "composition"
    }}>{`Composition`}</h2>
    <p>{`Styles can be composed by using an array of style objects in the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop.
This works the same way as `}<a parentName="p" {...{
        "href": "https://emotion.sh/docs/composition"
      }}>{`Emotion`}</a>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import React from 'react'
import { css } from 'theme-ui'

const base {
  backgroundColor: 'background',
  color: 'primary'
}

const danger = {
  color: 'red'
}

export default props => (
  <div>
    <div sx={base}>This will be the primary color</div>
    <div sx={[danger, base]}>
      This will be also be primary since the base styles
      overwrite the danger styles.
    </div>
    <div sx={[base, danger]}>This will be red</div>
  </div>
)
`}</code></pre>
    <h2 {...{
      "id": "functional-values"
    }}>{`Functional Values`}</h2>
    <p>{`For shorthand CSS properties or ones that are not automatically mapped to values in the theme,
use an inline function to reference values from the `}<inlineCode parentName="p">{`theme`}</inlineCode>{` object.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`/** @jsx jsx */
import { jsx } from 'theme-ui'

export default props => (
  <div
    {...props}
    sx={{
      boxShadow: theme => \`0 0 4px \${theme.colors.primary}\`,
    }}
  />
)
`}</code></pre>
    <h2 {...{
      "id": "child-selectors"
    }}>{`Child Selectors`}</h2>
    <p>{`In some cases you might want to apply styles to children of the current element.
You can do so with Emotion's `}<a parentName="p" {...{
        "href": "https://emotion.sh/docs/nested"
      }}>{`nested selectors`}</a>{`.`}</p>
    <p>{`If you want to reference the current class of the component, you can use the `}<a parentName="p" {...{
        "href": "https://emotion.sh/docs/object-styles#child-selectors"
      }}><inlineCode parentName="a">{`&`}</inlineCode></a>{` symbol.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`/** @jsx jsx */
import { jsx } from 'theme-ui'

export default props => (
  <div
    {...props}
    sx={{
      h1: {
        backgroundColor: 'tomato'
      },
      '& > div': {
        fontSize: [2, 3, 4]
      }
    }}
  />
)
`}</code></pre>
    <h2 {...{
      "id": "raw-css"
    }}>{`Raw CSS`}</h2>
    <p>{`To opt-out of using theme-based CSS, use the `}<inlineCode parentName="p">{`css`}</inlineCode>{` prop to render raw CSS values.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`/** @jsx jsx */
import { jsx } from 'theme-ui'

export default props => (
  <div
    {...props}
    css={{
      // raw values
      color: 'tomato',
      fontSize: 24,
    }}
  />
)
`}</code></pre>
    <h2 {...{
      "id": "using-the-sx-prop-in-mdx"
    }}>{`Using the `}<inlineCode parentName="h2">{`sx`}</inlineCode>{` Prop in MDX`}</h2>
    <p>{`Because MDX uses its own custom pragma and `}<inlineCode parentName="p">{`createElement`}</inlineCode>{` function, the Theme UI pragma will not work in MDX files.
You can use any of the `}<a parentName="p" {...{
        "href": "/components"
      }}>{`Theme UI components`}</a>{`,
which support the `}<inlineCode parentName="p">{`sx`}</inlineCode>{` prop, in an MDX file as a workaround.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-js"
      }}>{`<!-- Example -->

import { Box } from 'theme-ui'

<Box
  sx={{
    padding: 3,
    bg: 'highlight',
  }}>

# Hello

</Box>
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      